import { StyledBorrowingCalendarContent } from "./styles";
import clsx from "clsx";
import { useEffect, useRef } from "react";
import { format } from "date-fns";
import { useHistory } from "react-router-dom";
import { calculateReservationsInMonth } from "src/Utils/CalculateRecurringBorrowings";
import "moment/locale/ja";

interface IProps {
  data?: any;
  dateList?: any;
  holidays?: any;
  itemType: "vehicles" | "instruments";
}



export const BorrowingCalendarContent = (props: IProps) => {
  const { data, itemType, dateList, holidays } = props;
  //console.log("BorrowingCalendarContent data:", data);
  //console.log("BorrowingCalendarContent dateList:", dateList);

  const middleDate = dateList[Math.floor(dateList.length / 2)]; // e.g. "2025-11-15"
  const month = middleDate.slice(0, 7);  // "2025-11"

  const headerRef = useRef<HTMLTableRowElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const history = useHistory();


  useEffect(() => {
    if (!dateList || dateList.length === 0 || !tableContainerRef.current) return;
    const today = format(new Date(), 'yyyy-MM-dd');

    const targetCell = tableContainerRef.current.querySelector(`thead th[data-date="${today}"]`) as HTMLElement;

    if (targetCell) {
      const container = tableContainerRef.current;
      const stickyCol1Width = (container.querySelector('.leftSticky:nth-child(1)') as HTMLElement)?.offsetWidth || 0;
      const stickyCol2Width = (container.querySelector('.leftSticky:nth-child(2)') as HTMLElement)?.offsetWidth || 0;
      const stickyOffset = stickyCol1Width + stickyCol2Width + 10;

      const scrollLeft = targetCell.offsetLeft - stickyOffset;

      container.scrollTo({
        left: scrollLeft > 0 ? scrollLeft : 0, // Ensure scrollLeft is not negative
        behavior: "auto", // Use 'auto' for immediate jump on load
      });
    }
  }, [dateList]);

  const happensOnFilter = (occ: { date: string; endDate?: string }, day: string) => {
    const start = occ.date;
    const end = occ.endDate ?? occ.date; // single-day becomes a 1-day range
    return start <= day && day <= end;
  };

  return (
    <StyledBorrowingCalendarContent ref={tableContainerRef}>
      <div className="calendar">
        <table className="table">
          <thead>
          
            <tr className="table__header" ref={headerRef}>
              <th className="leftSticky title"></th>
              <th className="leftSticky title"></th>
              {dateList?.map((day, index) => ( 
                <th
                  key={index}
                  data-date={day} 
                  className={clsx(holidays?.includes(day) ? "dayOff" : "day")} 
                >
             
                  {typeof day === 'string' ? day.slice(8, 10).replace(/^0+/, '') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data?.map((item: any) => {
              const occurrences = calculateReservationsInMonth(month, item);
              const itemId = Array.isArray(item.item_ids)
                ? item.item_ids[0] ?? item.id
                : item.id;

              return (
                <tr key={item.id} className="">
                  <td className="leftSticky">
                    {(item.delayBorrowings?.length > 0 || item.borrowings?.some(b => b.status === 'red')) &&
                      <div className="delay">遅延</div>
                    }
                  </td>
                  <td className="leftSticky">
                    <span onClick={() => {history.push(`/${itemType}/calendar/${itemId}`);}}>{item?.name}</span>
                  </td>

                  {dateList.map((dateCell: any, dateIndex: number) => {
                    // const todayOccurrences = occurrences.filter((occ: any) => { return occ.date === dateCell; }); // this got multi-day events wrong
                    const todayOccurrences = occurrences.filter((occ: any) => happensOnFilter(occ, dateCell));
                    const hasOccurrence = todayOccurrences.length > 0;
                    const hasLate = todayOccurrences.some((occ: any) => occ.isLate);
                    const hasReturned = todayOccurrences.some((occ: any) => occ.isReturned);
                    const hasActive = todayOccurrences.some((occ: any) => !occ.isReturned && !occ.isLate);

                    const RED = "#ef4444";     // late
                    const BLUE = "#3b82f6";    // active
                    const GRAY = "#9ca3af";    // returned

                    const BASE = {
                      color: "#ffffff",
                      borderRadius: "0.25rem",
                      border: "1px solid rgba(0,0,0,0.15)",
                      boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.1)",
                    };

                    const calendarCellStyle = (hasLate: boolean, hasReturned: boolean, hasActive: boolean) =>
                    (!hasLate && !hasReturned && !hasActive) ? {} :

                    // singles
                    (hasLate && !hasReturned && !hasActive) ? { ...BASE, background: RED } :
                    (!hasLate && hasReturned && !hasActive) ? { ...BASE, background: GRAY } :
                    (!hasLate && !hasReturned && hasActive) ? { ...BASE, background: BLUE } :

                    // pairs (diagonal 50/50)
                    (hasLate && hasReturned && !hasActive) ? { ...BASE, background: `linear-gradient(135deg, ${RED} 0 50%, ${GRAY} 50% 100%)` } :
                    (hasLate && !hasReturned && hasActive) ? { ...BASE, background: `linear-gradient(135deg, ${RED} 0 50%, ${BLUE} 50% 100%)` } :
                    (!hasLate && hasReturned && hasActive) ? { ...BASE, background: `linear-gradient(135deg, ${BLUE} 0 50%, ${GRAY} 50% 100%)` } :

                    // triple (thirds)
                    { ...BASE, background: `linear-gradient(135deg, ${RED} 0 33.333%, ${BLUE} 33.333% 66.666%, ${GRAY} 66.666% 100%)` };

                    const showText = false;

                    return (
                      <td key={dateIndex} className="table__event"  onClick={() => {
                          history.push(`/${itemType}/calendar/${itemId}?date=${dateCell}`);
                        }}>
                        <div className="eventContainer">
                          {hasOccurrence ? (
                            <div className="event" style={calendarCellStyle(hasLate, hasReturned, hasActive)}>
                              {hasLate && showText ? <span>L</span> : null}
                              {hasReturned && showText ? <span>R</span> : null}
                              {hasActive && showText ? <span>A</span> : null}
                            </div>
                          ) : (
                              <div className="empty"/>
                            )}
                        </div>
                      </td>
                    );
                  })}

                  {dateList?.map((day, i) => { 

                    const borrowings = item.borrowings || [];

                    const isBlue = borrowings.some((b: any) => {
                      if (!b || b.status !== 'blue' || !b.from || !b.to) return false; 
                      try {
                          const from = new Date(b.from);
                          const to = new Date(b.to);
                          const current = new Date(day);
                          // Add validation for dates if necessary
                          if (isNaN(from.getTime()) || isNaN(to.getTime()) || isNaN(current.getTime())) return false;
                          return current >= from && current <= to;
                      } catch (e) {
                          console.error("Error parsing date:", b, day, e);
                          return false;
                      }
                    });

                    const isRed = borrowings.some((b: any) => {
                      if (!b || b.status !== 'red' || !b.from || !b.to) return false; 
                      try {
                          const current = new Date(day);
                          const from = new Date(b.from);
                          const to = new Date(b.to);
                          
                          if (isNaN(from.getTime()) || isNaN(to.getTime()) || isNaN(current.getTime())) return false;
                          return current >= from && current <= to;
                      } catch (e) {
                          console.error("Error parsing date:", b, day, e);
                          return false;
                      }
                    });

                    return null;
                    return (
                      <td key={i} className="table__event">
                        <div className="eventContainer">
                          {isBlue ? (
                            <div className="event">
                            
                            </div>
                          ) : isRed ? (
                            <div className="eventRed">
                          
                            </div>
                            ) : (
                              <div className="empty"></div>
                            )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </StyledBorrowingCalendarContent>
  );
};
